import { emptyArray } from '@/store/emptyValues';
import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { ValveControlOptions, DeviceWithCommandState, ValveCommand, ValveCommandState, Valve, DeviceSettingsUpdate } from '@/types/api';
import { apiClient } from '@/services/apiClient';
import { isInFlight, markInFlight, clearInFlight } from '@/store/inFlight';
import { logger } from '@/utils/logger';
// Type-only import to avoid a runtime cycle (store imports this slice).
import type { RootState } from '@/store';

export interface FormerDevice {
  device_id: number;
  dev_eui: string;
  name?: string | null;
  alias?: string | null;
  device_type?: string | null;
  assigned_at?: string | null;
  removed_at?: string | null;
  release_notes?: string | null;
  current_farm?: { farm_id: string; farm_name?: string | null } | null;
}

interface DeviceState {
  list: DeviceWithCommandState[];
  selected: DeviceWithCommandState | null;
  loading: boolean;
  error: string | null;
  lastUpdated: number;
  valveCommands: Record<string, ValveCommand>; // command_id -> command
  valves: Valve[]; // Store valve data for alias lookup
  valvesLoading: boolean;
  valvesError: string | null;
  // Phase 3: cache of former devices keyed by farm id (multi-farm tabs).
  formerByFarm: Record<string, FormerDevice[]>;
  formerLoading: boolean;
  formerError: string | null;
}

const initialState: DeviceState = {
  list: [],
  selected: null,
  loading: false,
  error: null,
  lastUpdated: 0,
  valveCommands: {},
  valves: [],
  valvesLoading: false,
  valvesError: null,
  formerByFarm: {},
  formerLoading: false,
  formerError: null,
};

// `Device.status` in this list means ONE thing: the adaptive connectivity status
// computed by `GET /farms/{id}/devices` from each device's own uplink cadence
// (`classify_silence` → online | delayed | offline).
//
// Every other endpoint that returns a device returns the raw DB column instead,
// whose vocabulary is different ("active", "inactive", …). Letting one of those
// into this list puts two vocabularies in one field, and consumers that switch on
// it break on a value that is not in their map — a crash in the layout editor's
// device list, triggered by adding a controller, because `POST /devices` answers
// with `status: "active"`.
//
// So the raw value is dropped at the boundary rather than translated: it is not a
// worse version of the adaptive status, it is a different fact, and the component
// already derives a sensible status from `last_seen` when this is absent.
const ADAPTIVE_STATUSES = ['online', 'delayed', 'offline'] as const;

function withAdaptiveStatusOnly<T extends { status?: string }>(device: T): T {
  if (device?.status && !ADAPTIVE_STATUSES.includes(device.status as any)) {
    const { status, ...rest } = device;
    return rest as T;
  }
  return device;
}

// Async thunks
export const fetchDevicesAsync = createAsyncThunk(
  'devices/fetchDevices',
  async (_, { rejectWithValue, dispatch }) => {
    try {
      // Fetch devices
      const devicesResponse = await apiClient.get('/devices/');

      // Also fetch valves for alias support - wait for it to complete
      const valvesResult = await dispatch(fetchValvesAsync());
      
      if (fetchValvesAsync.rejected.match(valvesResult)) {
        logger.warn('Valves fetch failed', valvesResult.payload);
      }
      
      return devicesResponse;
    } catch (error: any) {
      logger.error('Error fetching devices', error);
      logger.error('Error details', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch devices');
    }
  }
);

// Async thunk for fetching valves
export const fetchValvesAsync = createAsyncThunk(
  'devices/fetchValves',
  async (_, { rejectWithValue }) => {
    try {
      const response = await apiClient.get('/valves');
      
      // Handle different response formats
      let valvesData = response;
      if (response && typeof response === 'object') {
        if (Array.isArray(response)) {
          valvesData = response;
        } else if (response.data && Array.isArray(response.data)) {
          valvesData = response.data;
        } else if (response.valves && Array.isArray(response.valves)) {
          valvesData = response.valves;
        } else {
          logger.error('Unexpected response format', response);
          valvesData = [];
        }
      }
      
      return valvesData;
    } catch (error: any) {
      logger.error('Error fetching valves', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch valves');
    }
  }
);

// Async thunk for fetching devices by farm ID using the farm-specific endpoint
export const fetchDevicesByFarmAsync = createAsyncThunk(
  'devices/fetchDevicesByFarm',
  async (farmId: string, { rejectWithValue, dispatch }) => {
    const inFlightKey = `farmDevices:${farmId}`;
    markInFlight(inFlightKey);
    try {
      const response = await apiClient.get(`/farms/${farmId}/devices`);

      // Handle response format: { devices: [...], total: number }
      let devicesData = response;
      if (response && typeof response === 'object') {
        if (response.devices && Array.isArray(response.devices)) {
          devicesData = response.devices;
        } else if (Array.isArray(response)) {
          devicesData = response;
        } else {
          logger.error('fetchDevicesByFarmAsync: Unexpected response format', response);
          devicesData = [];
        }
      }
      
      // Also fetch valves for alias support - wait for it to complete
      const valvesResult = await dispatch(fetchValvesAsync());
      
      if (fetchValvesAsync.rejected.match(valvesResult)) {
        logger.warn('Valves fetch failed', valvesResult.payload);
      }
      
      return devicesData;
    } catch (error: any) {
      logger.error('Error fetching devices by farm', error);
      logger.error('Error details', {
        message: error.message,
        stack: error.stack,
        response: error.response
      });
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch devices for farm');
    } finally {
      clearInFlight(inFlightKey);
    }
  },
  { condition: (farmId: string) => !isInFlight(`farmDevices:${farmId}`) }
);

export const fetchDeviceAsync = createAsyncThunk(
  'devices/fetchDevice',
  async (devEui: string, { rejectWithValue }) => {
    try {
      const response = await apiClient.get(`/devices/${devEui}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch device');
    }
  }
);

export const controlValveAsync = createAsyncThunk(
  'devices/controlValve',
  async (
    { devEui, valve, action, options }: {
      devEui: string;
      valve: number;
      action: 'open' | 'close';
      options?: ValveControlOptions;
    },
    { rejectWithValue, dispatch, getState }
  ) => {
    try {
      // Resolve devEui → device.id from the Redux store. The device list is
      // already loaded by fetchDevicesAsync before any valve button can be
      // clicked (ValveNodeList / Dashboard mount), so this is a cache hit in
      // practice. We previously fetched the entire /devices/ list (~165
      // rows) on every valve click just to look up one id — that's the
      // single biggest source of the per-click backend log cascade.
      // Defensive fallback: if for any reason the device is not in the
      // store (deep-link, stale state, partial refresh), call the
      // single-device endpoint instead of re-fetching the whole list.
      const state = getState() as RootState;
      let device = state.devices.list.find(d => d.dev_eui === devEui) as
        | { id: number; dev_eui: string }
        | undefined;
      if (!device) {
        logger.warn(`controlValveAsync: device ${devEui} not in store, fetching single device`);
        try {
          device = (await apiClient.get(`/devices/${devEui}`)) as
            | { id: number; dev_eui: string }
            | undefined;
        } catch (lookupErr: any) {
          logger.error(`controlValveAsync: single-device fetch failed for ${devEui}: ${lookupErr?.message || lookupErr}`);
        }
      }

      if (!device) {
        return rejectWithValue(`Device ${devEui} not found`);
      }

      // Build context_data ONLY when the caller actually specified time/flow
      // control. The previous logic set time_control_enabled=true with
      // time_duration_seconds=0 whenever no options were passed (inverted
      // boolean), which fails the backend schema validation (ge=1 on
      // time_duration_seconds → 422). For the simple ValveControlButton
      // (manual fire-and-forget), we omit context_data entirely and let the
      // backend pick the right protocol semantics (close → cancel timer,
      // open → stay open until explicit close).
      const untilClosed = action === 'open' && options?.untilClosed === true;
      const hasTime = !untilClosed
        && (options?.hours !== undefined || options?.minutes !== undefined);
      const hasFlow = !untilClosed && options?.pulses !== undefined;
      const requestBody: Record<string, unknown> = {
        device_id: device.id,
        valve_number: valve,
        action: action,
      };
      if (untilClosed) {
        // No countdown: the valve stays open, and the supervisor leaves it
        // open, until someone closes it.
        requestBody.context_data = {
          notes: 'Manual control from UI (until closed)',
          until_closed: true,
        };
      } else if (hasTime || hasFlow) {
        const ctx: Record<string, unknown> = { notes: 'Manual control from UI' };
        if (hasTime) {
          ctx.time_control_enabled = true;
          ctx.time_duration_seconds = (options?.hours || 0) * 3600 + (options?.minutes || 0) * 60;
        }
        if (hasFlow) {
          ctx.flow_control_enabled = true;
          ctx.flow_volume_pulses = options?.pulses;
        }
        requestBody.context_data = ctx;
      }

      // Set processing state before making request
      dispatch(setValveCommandProcessing({ devEui, valveNumber: valve, isProcessing: true }));

      // No trailing slash — the route is @router.post("") so the canonical path
      // is /valve-commands. FastAPI's redirect_slashes=True would 307 a request
      // to /valve-commands/, and the body doesn't always survive that redirect
      // across CORS / axios versions. See memory `redirect_slashes=True` note.
      const response = await apiClient.post('/valve-commands', requestBody);
      return { devEui, response, valve, action };
    } catch (error: any) {
      // Clear processing state on error
      dispatch(setValveCommandProcessing({ devEui, valveNumber: valve, isProcessing: false }));
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to control valve');
    }
  }
);

// New async thunk for fetching valve command status
export const fetchValveCommandStatusAsync = createAsyncThunk(
  'devices/fetchValveCommandStatus',
  async (
    { commandId }: { commandId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.get(`/valve-commands/${commandId}`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to fetch valve command status');
    }
  }
);

// New async thunk for retrying failed valve commands
export const retryValveCommandAsync = createAsyncThunk(
  'devices/retryValveCommand',
  async (
    { commandId }: { commandId: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(`/valve-commands/${commandId}/retry`);
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to retry valve command');
    }
  }
);

// New async thunk for cancelling valve commands
export const cancelValveCommandAsync = createAsyncThunk(
  'devices/cancelValveCommand',
  async (
    { commandId, reason }: { commandId: string; reason?: string },
    { rejectWithValue }
  ) => {
    try {
      const response = await apiClient.post(`/valve-commands/${commandId}/cancel`, { reason });
      return response;
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to cancel valve command');
    }
  }
);

// Async thunk for adding a new device
export const addDevice = createAsyncThunk(
  'devices/addDevice',
  async (device: DeviceWithCommandState, { rejectWithValue }) => {
    try {
      return device;
    } catch (error: any) {
      logger.error('addDevice thunk: Error:', error);
      return rejectWithValue(error.response?.data?.detail || 'Failed to create device');
    }
  }
);

// Async thunk for updating device settings
export const updateDevice = createAsyncThunk(
  'devices/updateDevice',
  async ({ devEui, settings }: { devEui: string; settings: DeviceSettingsUpdate }, { rejectWithValue }) => {
    try {
      const response = await apiClient.updateDeviceSettings(devEui, settings);
      return response;
    } catch (error: any) {
      logger.error('updateDevice thunk: Error:', error);
      return rejectWithValue(error.response?.data?.detail || error.message || 'Failed to update device');
    }
  }
);

// Phase 3: transfer a device to a different farm. 409 with linked_sectors
// detail surfaces through rejectWithValue so the UI can render an actionable
// conflict screen instead of a generic error.
export const transferDeviceAsync = createAsyncThunk(
  'devices/transferDevice',
  async (
    args: {
      devEui: string;
      newFarmId: string;
      latitude?: number;
      longitude?: number;
      locationDescription?: string;
      notes?: string;
    },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.transferDevice(args.devEui, {
        new_farm_id: args.newFarmId,
        latitude: args.latitude,
        longitude: args.longitude,
        location_description: args.locationDescription,
        notes: args.notes,
      });
      return { devEui: args.devEui, newFarmId: args.newFarmId, response };
    } catch (error: any) {
      // Preserve the structured error (status + details.linked_sectors / source_farm)
      return rejectWithValue({
        status: error.status,
        message: error.message,
        details: error.details,
      });
    }
  },
);

// Phase 3: release a device from its current farm (orphan it).
export const releaseDeviceAsync = createAsyncThunk(
  'devices/releaseDevice',
  async (
    args: { devEui: string; notes?: string },
    { rejectWithValue },
  ) => {
    try {
      const response = await apiClient.releaseDevice(args.devEui, { notes: args.notes });
      return { devEui: args.devEui, response };
    } catch (error: any) {
      return rejectWithValue({
        status: error.status,
        message: error.message,
        details: error.details,
      });
    }
  },
);

// Phase 3: list former devices for a farm — feeds the "Former devices" section.
export const fetchFormerDevicesAsync = createAsyncThunk(
  'devices/fetchFormerDevices',
  async (farmId: string, { rejectWithValue }) => {
    const inFlightKey = `formerDevices:${farmId}`;
    markInFlight(inFlightKey);
    try {
      const response = await apiClient.getFormerDevices(farmId);
      return { farmId, former_devices: response.former_devices || [] };
    } catch (error: any) {
      return rejectWithValue(error.message || 'Failed to fetch former devices');
    } finally {
      clearInFlight(inFlightKey);
    }
  },
  { condition: (farmId: string) => !isInFlight(`formerDevices:${farmId}`) }
);

const deviceSlice = createSlice({
  name: 'devices',
  initialState,
  reducers: {
    selectDevice: (state, action: PayloadAction<DeviceWithCommandState>) => {
      state.selected = action.payload;
    },
    clearSelectedDevice: (state) => {
      state.selected = null;
    },
    updateDeviceStatus: (state, action: PayloadAction<{ devEui: string; updates: Partial<DeviceWithCommandState> }>) => {
      const { devEui, updates } = action.payload;
      const deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
      if (deviceIndex !== -1) {
        state.list[deviceIndex] = { ...state.list[deviceIndex], ...updates };
      }
      if (state.selected?.dev_eui === devEui) {
        state.selected = { ...state.selected, ...updates };
      }
    },
    clearError: (state) => {
      state.error = null;
    },
    // New reducers for valve command state management
    updateValveCommandState: (state, action: PayloadAction<{
      deviceId?: number;
      devEui?: string;
      valveNumber: number;
      command: ValveCommand;
      /**
       * Merge into the stored command when it is the same one (same id),
       * keeping fields the update does not carry — the WebSocket path, whose
       * messages know a state change but not the whole command. Without it
       * every transition erased the timeline's timestamps.
       */
      merge?: boolean;
    }>) => {
      const { deviceId, devEui, valveNumber, merge } = action.payload;
      let { command } = action.payload;
      

      
      // Find device by either device_id or dev_eui with improved matching
      let deviceIndex = -1;
      let foundDevice: DeviceWithCommandState | null = null;
      
      // Strategy 1: Match by numeric device_id
      if (deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device => device.id === deviceId);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // Strategy 2: Match by string device_id (devEui)
      if (!foundDevice && devEui !== undefined) {
        deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // Strategy 3: If deviceId looks like a devEui string (hex), try matching as devEui
      if (!foundDevice && deviceId !== undefined && typeof deviceId === 'string' && /^[0-9A-Fa-f]+$/.test(deviceId)) {
        deviceIndex = state.list.findIndex(device => device.dev_eui === deviceId);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // Strategy 4: String comparison fallback
      if (!foundDevice && deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device => 
          String(device.id) === String(deviceId) || 
          device.dev_eui === String(deviceId) ||
          String(device.id) === devEui ||
          device.dev_eui === devEui
        );
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      if (foundDevice) {
        
        // Initialize valve_commands if it doesn't exist
        if (!foundDevice.valve_commands) {
          foundDevice.valve_commands = {};
        }
        
        // Initialize valve command tracking for this valve
        if (!foundDevice.valve_commands[valveNumber]) {
          foundDevice.valve_commands[valveNumber] = {
            is_processing: false,
            last_updated: new Date().toISOString(),
          };
        }
        

        
        const previous = foundDevice.valve_commands[valveNumber].current_command;
        if (merge && previous && previous.command_id === command.command_id) {
          const defined = Object.fromEntries(
            Object.entries(command).filter(([, v]) => v !== undefined),
          ) as Partial<ValveCommand>;
          command = { ...previous, ...defined } as ValveCommand;
        }

        // Update the command state
        foundDevice.valve_commands[valveNumber].current_command = command;
        foundDevice.valve_commands[valveNumber].is_processing =
          command.current_state === 'manual_control_sent'
          || command.current_state === 'network_ack'
          || command.current_state === 'waiting';
        foundDevice.valve_commands[valveNumber].last_updated = new Date().toISOString();
        

        
        // Note: Command cleanup is now handled by components using useEffect
        // This ensures proper React lifecycle management and avoids side effects in reducers
        // Components should monitor command state and clear when appropriate
        
        // Store the command in the global valveCommands map
        state.valveCommands[command.command_id] = command;
        
        // Update selected device if it's the same device
        if (state.selected) {
          const isSameDevice =
            (deviceId !== undefined && state.selected.id === deviceId) ||
            (devEui !== undefined && state.selected.dev_eui === devEui) ||
            (deviceId !== undefined && state.selected.dev_eui === String(deviceId)) ||
            (deviceId !== undefined && String(state.selected.id) === String(deviceId)) ||
            (foundDevice.dev_eui === state.selected.dev_eui) ||
            (foundDevice.id === state.selected.id);
            
          if (isSameDevice) {
            if (!state.selected.valve_commands) {
              state.selected.valve_commands = {};
            }
            if (!state.selected.valve_commands[valveNumber]) {
              state.selected.valve_commands[valveNumber] = {
                is_processing: false,
                last_updated: new Date().toISOString(),
              };
            }
            state.selected.valve_commands[valveNumber] = foundDevice.valve_commands[valveNumber];
          }
        }
      } else {
        // Device not found, log error but don't crash
        logger.warn('Device not found for valve command state update');
      }
    },
    clearValveCommandState: (state, action: PayloadAction<{
      deviceId?: number;
      devEui?: string;
      valveNumber: number;
    }>) => {
      const { deviceId, devEui, valveNumber } = action.payload;
      
      // Find device by either device_id or dev_eui
      let deviceIndex = -1;
      let foundDevice = null;
      
      // First try to find by device_id if provided
      if (deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device => device.id === deviceId);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // If not found by device_id, try by dev_eui
      if (deviceIndex === -1 && devEui !== undefined) {
        deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // If still not found, try to match device_id as string (in case of type mismatch)
      if (deviceIndex === -1 && deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device =>
          String(device.id) === String(deviceId) || device.dev_eui === String(deviceId)
        );
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      if (foundDevice) {
        if (foundDevice.valve_commands?.[valveNumber]) {
          const commandId = foundDevice.valve_commands[valveNumber].current_command?.command_id;
          
          // Clear the command state
          foundDevice.valve_commands[valveNumber].current_command = undefined;
          foundDevice.valve_commands[valveNumber].is_processing = false;
          foundDevice.valve_commands[valveNumber].last_updated = new Date().toISOString();
          
          // Remove from the global valveCommands map
          if (commandId) {
            delete state.valveCommands[commandId];
          }
          
          // Update selected device if it's the same device
          if (state.selected) {
            const isSameDevice =
              (deviceId !== undefined && state.selected.id === deviceId) ||
              (devEui !== undefined && state.selected.dev_eui === devEui) ||
              (deviceId !== undefined && state.selected.dev_eui === String(deviceId)) ||
              (deviceId !== undefined && String(state.selected.id) === String(deviceId));
              
            if (isSameDevice && state.selected.valve_commands?.[valveNumber]) {
              state.selected.valve_commands[valveNumber].current_command = undefined;
              state.selected.valve_commands[valveNumber].is_processing = false;
              state.selected.valve_commands[valveNumber].last_updated = new Date().toISOString();
            }
          }
        }
      }
    },
    setValveCommandProcessing: (state, action: PayloadAction<{
      deviceId?: number;
      devEui?: string;
      valveNumber: number;
      isProcessing: boolean;
    }>) => {
      const { deviceId, devEui, valveNumber, isProcessing } = action.payload;
      
      // Find device by either device_id or dev_eui
      let deviceIndex = -1;
      let foundDevice = null;
      
      // First try to find by device_id if provided
      if (deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device => device.id === deviceId);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // If not found by device_id, try by dev_eui
      if (deviceIndex === -1 && devEui !== undefined) {
        deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      // If still not found, try to match device_id as string (in case of type mismatch)
      if (deviceIndex === -1 && deviceId !== undefined) {
        deviceIndex = state.list.findIndex(device =>
          String(device.id) === String(deviceId) || device.dev_eui === String(deviceId)
        );
        if (deviceIndex !== -1) {
          foundDevice = state.list[deviceIndex];
        }
      }
      
      if (foundDevice) {
        // Initialize valve_commands if it doesn't exist
        if (!foundDevice.valve_commands) {
          foundDevice.valve_commands = {};
        }
        
        // Initialize valve command tracking for this valve
        if (!foundDevice.valve_commands[valveNumber]) {
          foundDevice.valve_commands[valveNumber] = {
            is_processing: false,
            last_updated: new Date().toISOString(),
          };
        }
        
        // Update the processing state
        foundDevice.valve_commands[valveNumber].is_processing = isProcessing;
        foundDevice.valve_commands[valveNumber].last_updated = new Date().toISOString();
        
        // Update selected device if it's the same device
        if (state.selected) {
          const isSameDevice =
            (deviceId !== undefined && state.selected.id === deviceId) ||
            (devEui !== undefined && state.selected.dev_eui === devEui) ||
            (deviceId !== undefined && state.selected.dev_eui === String(deviceId)) ||
            (deviceId !== undefined && String(state.selected.id) === String(deviceId));
            
          if (isSameDevice) {
            if (!state.selected.valve_commands) {
              state.selected.valve_commands = {};
            }
            if (!state.selected.valve_commands[valveNumber]) {
              state.selected.valve_commands[valveNumber] = {
                is_processing: false,
                last_updated: new Date().toISOString(),
              };
            }
            state.selected.valve_commands[valveNumber].is_processing = isProcessing;
            state.selected.valve_commands[valveNumber].last_updated = new Date().toISOString();
          }
        }
      }
    },
    // Valve-related reducers
    setValves: (state, action: PayloadAction<Valve[]>) => {
      state.valves = action.payload;
      state.valvesError = null;
    },
    updateValve: (state, action: PayloadAction<Valve>) => {
      const valveIndex = state.valves.findIndex(v => v.id === action.payload.id);
      if (valveIndex !== -1) {
        state.valves[valveIndex] = action.payload;
      } else {
        state.valves.push(action.payload);
      }
    },
  },
  extraReducers: (builder) => {
    builder
      // Fetch valves
      .addCase(fetchValvesAsync.pending, (state) => {
        state.valvesLoading = true;
        state.valvesError = null;
      })
      .addCase(fetchValvesAsync.fulfilled, (state, action) => {
        state.valvesLoading = false;
        state.valves = action.payload;
      })
      .addCase(fetchValvesAsync.rejected, (state, action) => {
        state.valvesLoading = false;
        state.valvesError = action.payload as string;
      })
      // Fetch devices
      .addCase(fetchDevicesAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevicesAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchDevicesAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch devices by farm
      .addCase(fetchDevicesByFarmAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchDevicesByFarmAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.list = action.payload;
        state.lastUpdated = Date.now();
      })
      .addCase(fetchDevicesByFarmAsync.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Fetch single device
      .addCase(fetchDeviceAsync.fulfilled, (state, action) => {
        const device = withAdaptiveStatusOnly(action.payload);
        const deviceIndex = state.list.findIndex(d => d.dev_eui === device.dev_eui);
        if (deviceIndex !== -1) {
          state.list[deviceIndex] = device;
        } else {
          state.list.push(device);
        }
      })
      // Control valve
      .addCase(controlValveAsync.pending, (state) => {
        state.loading = true;
      })
      .addCase(controlValveAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Handle the three-state flow response
        const { devEui, response, valve, action: controlAction } = action.payload;
        const deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
        
        if (deviceIndex !== -1 && response.command_id) {
          // Create a command object for tracking
          const command = {
            command_id: response.command_id,
            device_id: parseInt(response.device_id || '0'),
            valve_number: valve,
            action: controlAction,
            current_state: 'manual_control_sent' as ValveCommandState,
            created_at: new Date().toISOString(),
            retry_count: 0,
            max_retries: 3,
            source: 'manual'
          };
          
          // Initialize valve_commands if it doesn't exist
          if (!state.list[deviceIndex].valve_commands) {
            state.list[deviceIndex].valve_commands = {};
          }
          
          // Initialize valve command tracking for this valve
          if (!state.list[deviceIndex].valve_commands[valve]) {
            state.list[deviceIndex].valve_commands[valve] = {
              is_processing: false,
              last_updated: new Date().toISOString(),
            };
          }
          
          // Update the command state
          state.list[deviceIndex].valve_commands[valve].current_command = command;
          state.list[deviceIndex].valve_commands[valve].is_processing = true;
          state.list[deviceIndex].valve_commands[valve].last_updated = new Date().toISOString();
          
          // Store the command in the global valveCommands map
          state.valveCommands[command.command_id] = command;
          
          // Update selected device if it's the same device
          if (state.selected?.dev_eui === devEui) {
            if (!state.selected.valve_commands) {
              state.selected.valve_commands = {};
            }
            if (!state.selected.valve_commands[valve]) {
              state.selected.valve_commands[valve] = {
                is_processing: false,
                last_updated: new Date().toISOString(),
              };
            }
            state.selected.valve_commands[valve] = state.list[deviceIndex].valve_commands[valve];
          }
        }
      })
      .addCase(controlValveAsync.rejected, (state, action) => {
        state.loading = false;
        // Backend `detail` is normally a string but can be a structured
        // object (offline-during-command sometimes carries a payload). Keep
        // state.error as a string so any rendering of state.devices.error
        // doesn't crash with "Objects are not valid as a React child".
        const payload = action.payload;
        if (typeof payload === 'string') {
          state.error = payload;
        } else if (payload && typeof payload === 'object' && 'message' in payload) {
          state.error = String((payload as { message: unknown }).message);
        } else {
          state.error = 'Valve control failed';
        }
        
        // Clear processing state on error
        const { devEui, valve } = action.meta.arg;
        const deviceIndex = state.list.findIndex(device => device.dev_eui === devEui);
        
        if (deviceIndex !== -1 && state.list[deviceIndex].valve_commands?.[valve]) {
          state.list[deviceIndex].valve_commands[valve].is_processing = false;
          state.list[deviceIndex].valve_commands[valve].last_updated = new Date().toISOString();
          
          if (state.selected?.dev_eui === devEui && state.selected.valve_commands?.[valve]) {
            state.selected.valve_commands[valve].is_processing = false;
            state.selected.valve_commands[valve].last_updated = new Date().toISOString();
          }
        }
      })
      // Fetch valve command status
      .addCase(fetchValveCommandStatusAsync.fulfilled, (state, action) => {
        const command = action.payload;
        state.valveCommands[command.command_id] = command;
        
        // Update device valve command state
        const deviceIndex = state.list.findIndex(device => device.id === command.device_id);
        if (deviceIndex !== -1) {
          const valveNumber = command.valve_number;
          
          if (!state.list[deviceIndex].valve_commands) {
            state.list[deviceIndex].valve_commands = {};
          }
          
          if (!state.list[deviceIndex].valve_commands[valveNumber]) {
            state.list[deviceIndex].valve_commands[valveNumber] = {
              is_processing: false,
              last_updated: new Date().toISOString(),
            };
          }
          
          state.list[deviceIndex].valve_commands[valveNumber].current_command = command;
          state.list[deviceIndex].valve_commands[valveNumber].is_processing =
            command.current_state === 'manual_control_sent' || command.current_state === 'waiting';
          state.list[deviceIndex].valve_commands[valveNumber].last_updated = new Date().toISOString();
        }
      })
      // Retry valve command
      .addCase(retryValveCommandAsync.fulfilled, (state, action) => {
        const command = action.payload;
        state.valveCommands[command.command_id] = command;
        
        // Update device valve command state
        const deviceIndex = state.list.findIndex(device => device.id === command.device_id);
        if (deviceIndex !== -1) {
          const valveNumber = command.valve_number;
          
          if (state.list[deviceIndex].valve_commands?.[valveNumber]) {
            state.list[deviceIndex].valve_commands[valveNumber].current_command = command;
            state.list[deviceIndex].valve_commands[valveNumber].is_processing =
              command.current_state === 'manual_control_sent' || command.current_state === 'waiting';
            state.list[deviceIndex].valve_commands[valveNumber].last_updated = new Date().toISOString();
          }
        }
      })
      // Cancel valve command
      .addCase(cancelValveCommandAsync.fulfilled, (state, action) => {
        const command = action.payload;
        state.valveCommands[command.command_id] = command;
        
        // Update device valve command state
        const deviceIndex = state.list.findIndex(device => device.id === command.device_id);
        if (deviceIndex !== -1) {
          const valveNumber = command.valve_number;
          
          if (state.list[deviceIndex].valve_commands?.[valveNumber]) {
            state.list[deviceIndex].valve_commands[valveNumber].current_command = command;
            state.list[deviceIndex].valve_commands[valveNumber].is_processing = false;
            state.list[deviceIndex].valve_commands[valveNumber].last_updated = new Date().toISOString();
          }
        }
      })
      // Add device
      .addCase(addDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(addDevice.fulfilled, (state, action) => {
        state.loading = false;
        state.list.push(withAdaptiveStatusOnly(action.payload));
        state.error = null;
      })
      .addCase(addDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Update device
      .addCase(updateDevice.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(updateDevice.fulfilled, (state, action) => {
        state.loading = false;
        // Update device in list
        const index = state.list.findIndex(d => d.dev_eui === action.payload.dev_eui);
        if (index !== -1) {
          // Merged, so the raw status must be stripped from the INCOMING half or
          // it overwrites the adaptive one already in the list.
          state.list[index] = {
            ...state.list[index], ...withAdaptiveStatusOnly(action.payload),
          };
        }
        state.error = null;
      })
      .addCase(updateDevice.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      })
      // Phase 3: transfer
      .addCase(transferDeviceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(transferDeviceAsync.fulfilled, (state, action) => {
        state.loading = false;
        // Remove the transferred device from the current list view — caller's
        // UI will either refetch by farm or observe this removal.
        state.list = state.list.filter((d) => d.dev_eui !== action.payload.devEui);
        state.error = null;
      })
      .addCase(transferDeviceAsync.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || 'Failed to transfer device';
      })
      // Phase 3: release
      .addCase(releaseDeviceAsync.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(releaseDeviceAsync.fulfilled, (state, action) => {
        state.loading = false;
        state.list = state.list.filter((d) => d.dev_eui !== action.payload.devEui);
        state.error = null;
      })
      .addCase(releaseDeviceAsync.rejected, (state, action) => {
        state.loading = false;
        const payload = action.payload as { message?: string } | undefined;
        state.error = payload?.message || 'Failed to release device';
      })
      // Phase 3: former devices
      .addCase(fetchFormerDevicesAsync.pending, (state) => {
        state.formerLoading = true;
        state.formerError = null;
      })
      .addCase(fetchFormerDevicesAsync.fulfilled, (state, action) => {
        state.formerLoading = false;
        state.formerByFarm[action.payload.farmId] = action.payload.former_devices;
      })
      .addCase(fetchFormerDevicesAsync.rejected, (state, action) => {
        state.formerLoading = false;
        state.formerError = (action.payload as string) || 'Failed to fetch former devices';
      });
  },
});

export const {
  selectDevice,
  clearSelectedDevice,
  updateDeviceStatus,
  clearError,
  updateValveCommandState,
  clearValveCommandState,
  setValveCommandProcessing,
  setValves,
  updateValve,
} = deviceSlice.actions;

// Selectors
export const selectAllValves = (state: { devices: DeviceState }) => state.devices.valves;
export const selectValvesByDeviceId = (deviceId: number) => (state: { devices: DeviceState }) =>
  state.devices.valves.filter(valve => valve.device_id === deviceId);
export const selectValveById = (valveId: string) => (state: { devices: DeviceState }) =>
  state.devices.valves.find(valve => valve.id === valveId);
export const selectValveAlias = (deviceId: number, valveNumber: number) => (state: { devices: DeviceState }) => {
  const valve = state.devices.valves.find(v => v.device_id === deviceId && v.valve_number === valveNumber);
  return valve?.name;
};
export const selectDevEuiExists = (state: { devices: DeviceState }, devEui: string): boolean => {
  return state.devices.list.some(device => device.dev_eui === devEui);
};
export const selectFormerDevices = (farmId: string) => (state: { devices: DeviceState }) =>
  state.devices.formerByFarm[farmId] || emptyArray<FormerDevice>();
export const selectFormerDevicesLoading = (state: { devices: DeviceState }) =>
  state.devices.formerLoading;
export default deviceSlice.reducer;